import { useEffect, useState } from "react";
import { useSession } from "@/security/RouteGuards";
import useProxyStatus from "@/hooks/useProxyStatus";
import {
  useDeleteQueryParams,
  useShowQuickStartUrlState,
} from "@/hooks/urlState";
import { ProxyConfigStatusType } from "@/types/ProxyConfigurationTypes";
import QuickStartOnboardingDialog from "./QuickStartOnboardingDialog";

/**
 * First-request "Quick Start" onboarding modal (r-1139).
 *
 * Shown on the dashboard immediately after registration: the register / Google
 * SSO / activation flows redirect to `/dashboard?showQuickStart=true`. The
 * `showQuickStart` query param is the one-shot trigger — it is only ever set by
 * the post-register redirect and is cleared (via `replace`) on close, so the
 * modal shows once after sign-up and never reappears on later dashboard visits.
 *
 * The param also survives in bookmarks and browser-suggested URLs, so it is not
 * trusted on its own: users on a paid plan or who have already sent a proxy
 * request never see the modal.
 */
export default function QuickStartOnboarding() {
  const { plan } = useSession();
  const [showQuickStart] = useShowQuickStartUrlState();
  const deleteQueryParams = useDeleteQueryParams();
  const [open, setOpen] = useState(false);

  // Only fetch (and only ever show) for free-plan users arriving from the
  // post-register redirect.
  const enabled = Boolean(showQuickStart) && plan?.proxy_type === "free";
  const planId = plan?.id || "default";
  const { data } = useProxyStatus(planId, enabled, {
    revalidateOnFocus: false,
  });
  const credentials = unusedProxyCredentials(data);

  useEffect(() => {
    if (credentials) {
      setOpen(true);
    }
  }, [credentials]);

  const handleClose = () => {
    setOpen(false);
    // Clear the trigger with `replace` so the back button can't restore the
    // `?showQuickStart=true` URL and reopen the modal.
    deleteQueryParams(["showQuickStart"], "replace");
  };

  if (!credentials) {
    return null;
  }

  return (
    <QuickStartOnboardingDialog
      open={open}
      onClose={handleClose}
      proxyUserName={credentials.username}
      password={credentials.password}
      planId={planId}
    />
  );
}

function unusedProxyCredentials(status: ProxyConfigStatusType | undefined) {
  return status?.is_proxy_used === false ? status : undefined;
}
